import { useMutation, useQueryCache } from "react-query";
import {
  postPermitContactInvite,
  postPermitContactAcceptInvite,
  postPermitContactRevokeInvite,
} from "../../../queries/permits";
import { useToasts } from "react-toast-notifications";
import { get } from "lodash";
import { useState } from "react";
import update from "immutability-helper";
import usePermit from "./permits/usePermit";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";

interface IUsePermitContactInviteProps {
  permitId: number;
  contactId: number;
}

type Contact = Partial<{
  address: string;
  address2: string;
  city: string;
  company_name: string;
  contact_id: string;
  contact_type: string;
  discrepancies: unknown[];
  first_name: string;
  id: number;
  email: string;
  has_access: boolean;
  invited_at: string;
  is_applicant: boolean;
  is_active: boolean;
  user_id: number;
  is_discrepancies_ignored: boolean;
  is_discrepancies_ignored_before: boolean;
  is_submitter: boolean;
  last_name: string;
  license_number: string;
  permit_id: number;
  permits_extension_def_id: string;
  phone: string;
  physical_address: string;
  physical_address2: string;
  physical_city: string;
  physical_state: string;
  physical_zip: string;
  same_mail_and_physical_address: boolean;
  state: string;
  verification_sent_at: unknown;
  verification_token: unknown;
  verified_at: unknown;
  zip: string;
}>;

function getContact(permit: any, contactId: Number) {
  const contacts = get<Contact[]>(permit, "data.attributes.permits-contacts", []);
  return contacts.find((contact: Contact) => contact.id === contactId);
}

function immutablyUpdatePermitWithContactInviteStatus(permit: any, newContactData: any) {
  const oldContacts = permit.data.attributes["permits-contacts"];
  const newPermit = update(permit, {
    data: {
      attributes: {
        "permits-contacts": {
          $set: oldContacts.map((contact: Contact) => {
            if (contact.id === parseInt(newContactData.data.id)) {
              return {
                ...contact,
                invited_at: newContactData.data.attributes["invited-at"],
                has_access: newContactData.data.attributes["has-access"],
                email: newContactData.data.attributes["email"],
              };
            }
            return contact;
          }),
        },
      },
    },
  });
  return newPermit;
}

export default function usePermitContactInvite({ permitId, contactId }: IUsePermitContactInviteProps) {
  // Using state to track loading state of mutations because we also need to wait for the permit query invalidation, not just the mutation
  const [mutateLoading, setMutateLoading] = useState(false);

  const { addToast } = useToasts();
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();

  const permitQuery = usePermit({ permitId: permitId });
  const contact = getContact(permitQuery.data, contactId);
  const contactsWithSameUser = getContactsWithSameUser(contact, permitQuery.data);

  const loadingContact = permitQuery.isLoading;
  const missingEmail = !contact?.email;
  const isApplicant = contact?.is_applicant || contactsWithSameUser.some((contact) => contact?.is_applicant);
  const hasAccess = contact?.has_access;
  const invited = !!contact?.invited_at;

  const showGrantAccess = !missingEmail && !hasAccess && !invited && !isApplicant;
  const showGrantAccessNoEmail = missingEmail && !hasAccess && !invited && !isApplicant;
  const showCancelInvite = !missingEmail && !hasAccess && invited && !isApplicant;
  const showRevokeAccess = !missingEmail && hasAccess && invited && !isApplicant;

  const [inviteMutation] = useMutation(postPermitContactInvite(networkHeaders), {
    onMutate: () => {
      setMutateLoading(true);
    },
    onSuccess: async (data) => {
      queryCache.setQueryData(["permits", String(permitId)], (permit: any) => {
        return immutablyUpdatePermitWithContactInviteStatus(permit, data);
      });

      const permit = queryCache.getQueryData(["permits", String(permitId)]);
      const contact = getContact(permit, contactId);
      const hasAccess = contact?.has_access;
      const message = hasAccess ? "Access granted" : "Invite sent";
      addToast(message, { appearance: "success", autoDismiss: true });
      setMutateLoading(false);
    },
    onError: () => {
      addToast("Invite failed", { appearance: "error", autoDismiss: true });
      setMutateLoading(false);
    },
  });
  const [revokeMutation] = useMutation(postPermitContactRevokeInvite(networkHeaders), {
    onMutate: () => {
      setMutateLoading(true);
    },
    onSuccess: async (data) => {
      // Using old data on purpose here
      const permit = queryCache.getQueryData(["permits", String(permitId)]);
      const contact = getContact(permit, contactId);
      const hasAccess = contact?.has_access;
      const message = hasAccess ? "Access revoked" : "Invite revoked";

      queryCache.setQueryData(["permits", String(permitId)], (permit: any) => {
        return immutablyUpdatePermitWithContactInviteStatus(permit, data);
      });

      queryCache.invalidateQueries(["conversation", permitId.toString()]); // Don't need to wait for this to finish

      addToast(message, { appearance: "success", autoDismiss: true });
      setMutateLoading(false);
    },
    onError: () => {
      addToast("Invite revoke failed", { appearance: "error", autoDismiss: true });
      setMutateLoading(false);
    },
  });
  const [acceptMutation] = useMutation(postPermitContactAcceptInvite(networkHeaders), {
    onMutate: () => {
      setMutateLoading(true);
    },
    onSuccess: async (data) => {
      queryCache.setQueryData(["permits", String(permitId)], (permit: any) => {
        return immutablyUpdatePermitWithContactInviteStatus(permit, data);
      });

      queryCache.invalidateQueries(["conversation", permitId.toString()]); // Don't need to wait for this to finish

      addToast("Invite accepted", { appearance: "success", autoDismiss: true });
      setMutateLoading(false);
    },
    onError: () => {
      addToast("Failed to accept invite", { appearance: "error", autoDismiss: true });
      setMutateLoading(false);
    },
  });

  return {
    invite: (email?: string) => inviteMutation({ permitId, contactId, email }),
    revoke: () => revokeMutation({ permitId, contactId }),
    accept: () => acceptMutation({ permitId, contactId }),
    loading: loadingContact || mutateLoading,
    showGrantAccess,
    showGrantAccessNoEmail,
    showCancelInvite,
    showRevokeAccess,
  };
}

/**
 * Gets all permit-contacts with the same user_id as the given permit-contact.
 *
 * @detail
 *
 * There was a change made awhile back where multiple permit-contacts can be associated with
 * the same user (they have the same user_id/person). Since then, there have been some ripple effects
 * across the app in places where we assumed a 1-to-1 relationship between a "person" in the
 * "persons" table and a "permit-contact".
 *
 * @param contact the permit contact
 * @param permit the permit the permit contact is associated with
 * @returns an array of contacts with the same user_id as the given contact
 */
function getContactsWithSameUser(contact: Contact | undefined, permit: any): Contact[] {
  // Explicitly checking for null/undefined in case user_id is "0"
  if (!contact || contact.user_id === undefined || contact.user_id === null) {
    return [];
  }

  const contactUserId = contact.user_id;
  const contacts = get<Contact[]>(permit, "data.attributes.permits-contacts", []);

  return contacts
    .filter((permitContact) => {
      return permitContact.user_id !== undefined || permitContact.user_id !== null;
    })
    .filter((permitContact) => {
      return permitContact.user_id === contactUserId;
    });
}
